import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardWrapper from '../components/DashboardWrapper';
import RetailerOfferView from '../components/RetailerOfferView';
import { Button } from '../components/ui/button';
import { ArrowLeft, Tag, Loader2 } from 'lucide-react';
import api from '../lib/api';

/**
 * OfferDetailPage - Page de détail d'une offre (Retailer)
 * 
 * Utilise le composant unifié RetailerOfferView en mode="live"
 * 
 * SÉCURISÉ V3 - Gestion robuste de tous les cas:
 * - CAS 1: product_ids spécifiques → fetch et filtre
 * - CAS 2: apply_to_all_products → fetch produits du partenaire
 * - CAS 3: invalide (aucun produit configuré) → message explicatif
 */
const OfferDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [products, setProducts] = useState([]);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productLoadingCase, setProductLoadingCase] = useState(null);

  useEffect(() => {
    loadOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOffer = async () => {
    setLoading(true);
    setError(null);
    setProductLoadingCase(null);
    
    try {
      // Charger l'offre depuis l'API publique
      const offersResponse = await api.get('/offers/public/active?limit=100');
      const allOffers = offersResponse.data || [];
      const foundOffer = allOffers.find(o => o.id === id);
      
      if (!foundOffer) {
        setError('Offre non trouvée');
        setLoading(false);
        return;
      }
      
      setOffer(foundOffer);
      
      // Déterminer le cas de chargement des produits
      const hasSpecificProducts = foundOffer.product_ids && foundOffer.product_ids.length > 0;
      const isAllPartnerProducts = foundOffer.apply_to_all_products === true;
      const hasPartnerId = !!foundOffer.partner_id;
      
      // CAS 1: Produits spécifiques
      if (hasSpecificProducts) {
        setProductLoadingCase('specific_ids');
        try {
          const productsResponse = await api.get('/products?limit=200');
          const rawAll = productsResponse.data || [];
          const allProducts = Array.isArray(rawAll) ? rawAll : (rawAll?.products || []);
          const linkedProducts = allProducts.filter(p => foundOffer.product_ids.includes(p.id));
          setProducts(linkedProducts);
        } catch (err) {
          console.error('[OfferDetail] Erreur chargement produits spécifiques:', err.message);
          setProductLoadingCase('error');
        }
      }
      // CAS 2: Tous les produits du partenaire
      else if (isAllPartnerProducts && hasPartnerId) {
        setProductLoadingCase('all_partner');
        try {
          const productsResponse = await api.get(`/products?partner_id=${foundOffer.partner_id}&limit=50`);
          const rawPart = productsResponse.data || [];
          const partnerProducts = Array.isArray(rawPart) ? rawPart : (rawPart?.products || []);
          setProducts(partnerProducts);
        } catch (err) {
          console.error('[OfferDetail] Erreur chargement produits partenaire:', err.message);
          setProductLoadingCase('error');
        }
      }
      // CAS 3: Configuration invalide
      else {
        setProductLoadingCase('no_products');
        setProducts([]);
      }
      
      // Charger le partenaire
      if (hasPartnerId) {
        try {
          const partnerResponse = await api.get(`/partners/${foundOffer.partner_id}`);
          setPartner(partnerResponse.data);
        } catch (err) {
          console.error('[OfferDetail] Erreur chargement partenaire:', err.message);
        }
      }
      
    } catch (err) {
      console.error('[OfferDetail] Erreur critique:', err.message);
      setError('Impossible de charger l\'offre');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardWrapper>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      </DashboardWrapper>
    );
  }

  if (error || !offer) {
    return (
      <DashboardWrapper>
        <div className="max-w-2xl mx-auto text-center py-20">
          <Tag className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Offre non trouvée</h1>
          <p className="text-muted-foreground mb-6">{error || 'Cette offre n\'existe pas ou n\'est plus disponible.'}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      {/* Header avec bouton retour - aligné avec le layout full-width */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          data-testid="offer-back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
      </div>
      
      {/* Composant unifié en mode live */}
      <RetailerOfferView
        offer={offer}
        products={products}
        partner={partner}
        mode="live"
        productLoadingCase={productLoadingCase}
        onRetry={loadOffer}
      />
    </DashboardWrapper>
  );
};

export default OfferDetailPage;
